import React, { useEffect, useState } from "react";
import { Box, Button, Stack, TextField, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";

import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";

import PageHeader from "../../components/common/PageHeader";
import DataTable from "../../components/common/DataTable";

import { COMMON_STRINGS } from "../../constants/strings";

// 🔥 API
import { getServices, updateService } from "../../services/api";

// 🔥 Redux
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice";

import "../table-common.css";

const ServicesList = () => {
  const dispatch = useDispatch();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [editId, setEditId] = useState<string | null>(null);
  const [editPrice, setEditPrice] = useState<number | string>("");
  const [editDuration, setEditDuration] = useState<number | string>("");

  // ================= FETCH =================
  const fetchServices = async () => {
    try {
      setLoading(true);

      const res = await getServices();

      const flatData: any[] = [];

      (res.data.data || []).forEach((cat: any) => {
        cat.subCategories.forEach((sub: any) => {
          sub.subSubCategories.forEach((subSub: any) => {
            subSub.services.forEach((srv: any) => {
              flatData.push({
                ...srv,
                name: subSub.name,
                category: cat.category,
                subCategory: sub.name,
              });
            });
          });
        });
      });

      setRows(flatData);
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  // ================= UPDATE =================
  const handleSave = async (row: any) => {
    try {
      await updateService(row._id, {
        category: row.category,
        subCategory: row.subCategory,
        subSubCategory: row.name,
        description: row.description,
        price: Number(editPrice),
        duration: Number(editDuration),
        isActive: row.isActive,
      });

      // 🔥 LOCAL UPDATE (NO REFRESH JUMP)
      setRows((prev) =>
        prev.map((item) =>
          item._id === row._id
            ? {
                ...item,
                price: Number(editPrice),
                duration: Number(editDuration),
              }
            : item,
        ),
      );

      dispatch(
        showSnackbar({
          message: "Service updated successfully",
          type: "success",
        }),
      );

      setEditId(null);
      setEditPrice("");
      setEditDuration("");
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
          type: "error",
        }),
      );
    }
  };

  // ================= CANCEL =================
  const handleCancel = () => {
    setEditId(null);
    setEditPrice("");
    setEditDuration("");
  };

  // ================= FILTER =================
  const filteredRows = rows.filter((row) =>
    Object.values(row)
      .join(" ")
      .toLowerCase()
      .includes(searchText.toLowerCase()),
  );

  // ================= COLUMNS =================
  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Service Name",
      flex: 1,
    },
    {
      field: "category",
      headerName: "Category",
      flex: 1,
    },
    {
      field: "subCategory",
      headerName: "Sub Category",
      flex: 1,
    },
    {
      field: "price",
      headerName: "Price (₹)",
      flex: 1,
      renderCell: (params) => {
        const isEditing = editId === params.row._id;

        return isEditing ? (
          <TextField
            size="small"
            type="number"
            value={editPrice}
            onChange={(e) => setEditPrice(e.target.value)}
            sx={{ width: "80px" }}
          />
        ) : (
          `₹ ${params.value}`
        );
      },
    },
    {
      field: "duration",
      headerName: "Duration (min)",
      flex: 1,
      renderCell: (params) => {
        const isEditing = editId === params.row._id;

        return isEditing ? (
          <TextField
            size="small"
            type="number"
            value={editDuration}
            onChange={(e) => setEditDuration(e.target.value)}
            sx={{ width: "80px" }}
          />
        ) : (
          `${params.value} min`
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const isEditing = editId === params.row._id;

        return (
          <Box className="action-cell">
            {isEditing ? (
              <Stack direction="row" spacing={0.5}>
                {/* 💾 SAVE */}
                <Tooltip title="Save changes">
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleSave(params.row)}
                      sx={{
                        minWidth: "32px",
                        padding: "6px",
                        backgroundColor: "var(--success)",
                      }}
                    >
                      <SaveIcon fontSize="small" />
                    </Button>
                  </span>
                </Tooltip>

                {/* ❌ CANCEL */}
                <Tooltip title="Cancel edit">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleCancel}
                      sx={{
                        minWidth: "32px",
                        padding: "6px",
                        color: "var(--danger)",
                        borderColor: "var(--danger)",
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            ) : (
              <Stack direction="row" spacing={0.5}>
                {/* ✏️ EDIT */}
                <Tooltip title="Edit service">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setEditId(params.row._id);
                        setEditPrice(params.row.price);
                        setEditDuration(params.row.duration);
                      }}
                      sx={{
                        minWidth: "32px",
                        padding: "6px",
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Services"
        searchValue={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder="Search services..."
      />

      <DataTable
        rows={filteredRows}
        columns={columns}
        loading={loading}
        getRowId={(row) => row._id}
      />
    </Box>
  );
};

export default ServicesList;
